import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { mapAddress } from '@general/js/utils/JoinNow';
import { MyCircumstances, RebateTier } from '@general/js/enums';
import { OperationType } from '@/enums';
import { calculateAge, reverseFormatFormDate } from '@general/js/utils/dateTimeUtils';
import { mapTerminationObj, mapPolicyHolderObj, mapPartnerObj, mapEligibilityObj, mapProductListObj, mapOtherPolicyUpdates } from './SubmitChangeCover';
import { findPolicyHolder } from './ExtraUtils';
import { getPartnerDetailsMP } from '../MemberUtils';

// eslint-disable-next-line import/prefer-default-export
export const submitChangeCover = (changeCoverEnteredData, captureInformationData, initialInformationData, existingMemberDetails, extraData = {}) => {
  // Composition
  const composition = {
    op: changeCoverEnteredData.step2.coverComposition !== extraData.currentComposition ? OperationType.REPLACE : OperationType.NONE,
    value: changeCoverEnteredData.step2.coverComposition,
  };

  // State
  const state = {
    op: captureInformationData.policyHolder.residentialAddress.state !== initialInformationData.policyHolder.residentialAddress.state ? OperationType.REPLACE : OperationType.NONE,
    value: captureInformationData.policyHolder.residentialAddress.state,
  };

  // Postcode
  const postcode = {
    op:
      captureInformationData.policyHolder.residentialAddress.postcode !== initialInformationData.policyHolder.residentialAddress.postcode
        ? OperationType.REPLACE
        : OperationType.NONE,
    value: captureInformationData.policyHolder.residentialAddress.postcode,
  };

  // Terminations
  const terminations = mapTerminationObj(captureInformationData.policyChanges, captureInformationData.partner, captureInformationData.children, extraData);

  // Residential Address
  const initialPolicyHolder = cloneDeep(initialInformationData.policyHolder);
  const currentPolicyHolder = cloneDeep(captureInformationData.policyHolder);
  const initialResidential = mapAddress(initialPolicyHolder.residentialAddress) ?? {};
  if (initialResidential.addressType) {
    delete initialResidential.addressType; // deletes address type to compare initial and current residential
  }
  const currentResidential = mapAddress(currentPolicyHolder.residentialAddress) ?? {};
  if (currentResidential.addressType) {
    delete currentResidential.addressType; // deletes address type to compare initial and current residential
  }

  const residentialAddress = {
    op: !isEqual(initialResidential, currentResidential) ? OperationType.REPLACE : OperationType.NONE,
    ...currentResidential,
  };

  // Policy holder
  const existingMainPolicyHolderDetails = findPolicyHolder(existingMemberDetails);
  const mainPolicyHolder = mapPolicyHolderObj(initialPolicyHolder, currentPolicyHolder, changeCoverEnteredData, existingMainPolicyHolderDetails);

  // Partner
  const existingPartnerDetails = getPartnerDetailsMP(existingMemberDetails, true);
  const initialPartner = cloneDeep(initialInformationData.partner);
  const currentPartner = cloneDeep(captureInformationData.partner);
  const partner = mapPartnerObj(initialPartner, currentPartner, changeCoverEnteredData, currentPolicyHolder, extraData, existingPartnerDetails);

  // Children
  const childrenObj = cloneDeep(captureInformationData.children);
  let children;
  if (childrenObj.payloads) {
    children = childrenObj.payloads.map((child) => {
      child.age = calculateAge(reverseFormatFormDate(child.dateOfBirth)); // add ages to all children in payload
      if (child.op.toLowerCase() !== OperationType.ADD) {
        // if child is not new, no need to include dateOfBirth
        delete child.dateOfBirth;
      }
      if (!child.transferFromOthers && child.previousFunds) {
        delete child.transferFromOthers;
        return {
          ...child,
          previousFunds: [],
        };
      }
      delete child.transferFromOthers;
      return child;
    });
  }

  // Eligibility
  const initialEligibility = cloneDeep(extraData.initialEligibilityData);
  const currentEligibility = cloneDeep(captureInformationData.eligibility);

  const eligibility = mapEligibilityObj(initialEligibility, currentEligibility, changeCoverEnteredData.step1.eligibility);

  // Rebate
  const rebate = cloneDeep(captureInformationData.rebate);
  const myCircumstances = rebate.myCircumstances || MyCircumstances.OTHER;
  delete rebate.myCircumstances;
  delete rebate.isFormPrefilled; // This field is only used for prefilling data

  // Medicare card will only return {} if no rebate applied
  // When rebate.medicareCard is not {}
  if (Object.keys(rebate.medicareCard).length && rebate.medicareCard.cardNumber) {
    rebate.medicareCard.medicareCardNumber = rebate.medicareCard.cardNumber;
    delete rebate.medicareCard.cardNumber;
  }

  // Other policy updates
  const otherPolicyUpdates = mapOtherPolicyUpdates(extraData.memberPolicies, currentPolicyHolder.residentialAddress, initialPolicyHolder.residentialAddress);

  // Product list
  const initialProductList = cloneDeep(changeCoverEnteredData.recommendedProducts.existingProducts);
  const currentProductList = cloneDeep(changeCoverEnteredData.selectedProducts);
  const initialExcess = extraData.currentExcess;
  const currentExcess = changeCoverEnteredData.selectedExcess;
  const productList = mapProductListObj(initialProductList, currentProductList, initialExcess, currentExcess);

  const submitPayload = {
    memberNumber: extraData.memberNumber,
    currentDate: changeCoverEnteredData.step1.startDate,
    changeStartDate: changeCoverEnteredData.step1.startDate,
    myCircumstances,
    composition,
    state,
    postcode,
    terminations,
    residentialAddress,
    mainPolicyHolder,
    partner: extraData.newCompositionHavePartner ? partner : null,
    children: extraData.newCompositionHaveChildren ? children : null,
    eligibility,
    rebate,
    otherPolicyUpdates,
    productList,
  };

  // When there is no existing rebate and not going to apply rebate, will not include rebate in the payload
  if ((extraData.initialRebateTier === RebateTier.NONE || extraData.initialRebateTier === null) && rebate.rebateTier === RebateTier.NONE) {
    delete submitPayload.rebate;
  }

  return submitPayload;
};
